//@flow
import {useEffect, useState} from 'react';
import {
  createBlogCategoryService,
  deleteBlogCategoryService,
  editBlogCategoryService,
  listBlogCategoriesService,
} from '../promises/blog-category';

import {actions} from '../duck';
import {useActions} from '../../../utils/globalStore';

import type {BlogCategoryRequest} from '../api/blog-category';
import type {RootState} from '../../';
import {useSelector} from 'react-redux';

const categoryActions = {
  create: actions.addCategory,
  update: actions.updateCategory,
  delete: actions.deleteBlogCategory,
};

const blogCategorySelector = (state: RootState) => state.blog.categories;

export function useInitializeBlogCategories() {
  const [loading, setLoading] = useState(false);
  const categories = useSelector(blogCategorySelector);
  const setSavedCategories = useActions(actions.setSavedCategories);

  useEffect(() => {
    setLoading(true);
    listBlogCategoriesService()
      .then(categories => {
        setSavedCategories(categories);
      })
      .catch(() => {
        setSavedCategories([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setSavedCategories]);

  return {
    loading,
    categories,
  };
}

export function useBlogCategoriesAction() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const {create, update, delete: deleteCategory} = useActions(categoryActions);

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      setError(false);
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  const createCategory = (category: BlogCategoryRequest) => {
    setLoading(true);
    return createBlogCategoryService(category)
      .then(category => {
        create(category);
      })
      .catch(e => {
        setError(true);
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateCategory = (id: string, category: BlogCategoryRequest) => {
    setLoading(true);
    return editBlogCategoryService(id, category)
      .then(category => {
        update(category);
      })
      .catch(e => {
        setError(true);
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteBlogCategory = (id: string) => {
    setLoading(true);
    return deleteBlogCategoryService(id)
      .then(() => {
        deleteCategory(id);
      })
      .catch(e => {
        setError(true);
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    createCategory,
    updateCategory,
    deleteBlogCategory,
    loading,
    error,
  };
}

export function useBlogCategories() {
  const categories = useSelector(blogCategorySelector);

  return categories || [];
}

const categoryModalActions = {
  open: actions.openCategoriesModal,
  close: actions.closeCategoriesModal,
};

const modalOpenSelector = (state: RootState) => state.blog.categoryModalOpen;

export function useBlogCategoriesModal() {
  const {open, close} = useActions(categoryModalActions);

  const showModal = useSelector(modalOpenSelector);

  return {
    openModal: open,
    closeModal: close,
    showModal,
  };
}
